import React, { useEffect, useRef, useState } from "react";
import {
  reportEmptyState,
  reportList,
  reportListRow,
  reportPanel,
  reportPanelTitle,
  reportPanels,
  reportPanelsCollapsed,
  reportPanelsExpanded,
  reportTile,
  reportTileHeader,
  reportTileHeaderClickable,
  reportTileHeaderActions,
  reportTileHeaderButton,
  reportTileHeaderDate,
  reportTileWrap,
} from "../styles/DashboardStyles";
import { ReportData } from "interfaces/ReportData.interface";

interface ReportsTileProps {
  report: ReportData | null;
  loading?: boolean;
  selectedDate: string;
  onDateChange: (value: string) => void;
  autoCollapsed?: boolean;
}

const formatNumber = (value: number | null | undefined): string => {
  const safe = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  return safe.toLocaleString();
};

const ListRow: React.FC<{
  label: string;
  value?: number;
  total?: number;
  textValue?: string;
  prefix?: string;
}> = ({ label, value = 0, total, textValue, prefix }) => {
  const formattedValue = `${prefix || ""}${formatNumber(value)}`;
  const formattedTotal = `${prefix || ""}${formatNumber(total)}`;
  const rightText = textValue
    ? textValue
    : typeof total === "number"
      ? `${formattedValue}/${formattedTotal}`
      : formattedValue;

  return (
    <div style={reportListRow}>
      <span>{label}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#7A7A7A" }}>
        {rightText}
        <span style={{ fontSize: 16 }}></span>
      </span>
    </div>
  );
};

const ReportsTile: React.FC<ReportsTileProps> = ({
  report,
  loading,
  selectedDate,
  onDateChange,
  autoCollapsed = true,
}) => {
  const dateInputRef = useRef<HTMLInputElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(autoCollapsed);

  useEffect(() => {
    if (autoCollapsed) setCollapsed(true);
  }, [autoCollapsed]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = document.documentElement;

    const updateOffsets = () => {
      const reportHeight = widgetRef.current?.getBoundingClientRect().height ?? 0;
      const footerHeight = document.getElementById("copyright-banner")?.getBoundingClientRect().height ?? 0;
      root.style.setProperty("--report-widget-height", `${Math.ceil(reportHeight)}px`);
      root.style.setProperty("--footer-height", `${Math.ceil(footerHeight)}px`);
    };

    updateOffsets();
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => updateOffsets())
      : null;

    if (resizeObserver) {
      if (widgetRef.current) resizeObserver.observe(widgetRef.current);
      const footer = document.getElementById("copyright-banner");
      if (footer) resizeObserver.observe(footer);
    }

    window.addEventListener("resize", updateOffsets);
    return () => {
      window.removeEventListener("resize", updateOffsets);
      resizeObserver?.disconnect();
    };
  }, [collapsed, loading, report]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (nextValue) onDateChange(nextValue);
  };

  const handleOpenCalendar = () => {
    const input = dateInputRef.current;
    if (!input) return;
    const picker = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof picker.showPicker === "function") {
      picker.showPicker();
    } else {
      input.focus();
      input.click();
    }
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  if (loading) {
    return (
      <div style={reportTileWrap}>
        <div style={reportTile}>
          <div
            style={{ ...reportTileHeader, ...reportTileHeaderClickable }}
            role="button"
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
          >
            <span>Reports</span>
            <span
              style={reportTileHeaderActions}
              onClick={(event) => event.stopPropagation()}
            >
              <input
                ref={dateInputRef}
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                style={reportTileHeaderDate}
                aria-label="Report date"
              />
              <button
                type="button"
                onClick={handleOpenCalendar}
                style={reportTileHeaderButton}
                aria-label="Open calendar"
              >
                Calendar
              </button>
            </span>
          </div>
          <div style={{ ...reportPanels, ...(collapsed ? reportPanelsCollapsed : reportPanelsExpanded) }}>
            <div style={reportEmptyState}>Loading report data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div style={reportTileWrap}>
        <div style={reportTile}>
          <div
            style={{ ...reportTileHeader, ...reportTileHeaderClickable }}
            role="button"
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
          >
            <span>Reports</span>
            <span
              style={reportTileHeaderActions}
              onClick={(event) => event.stopPropagation()}
            >
              <input
                ref={dateInputRef}
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                style={reportTileHeaderDate}
                aria-label="Report date"
              />
              <button
                type="button"
                onClick={handleOpenCalendar}
                style={reportTileHeaderButton}
                aria-label="Open calendar"
              >
                Calendar
              </button>
            </span>
          </div>
          <div style={{ ...reportPanels, ...(collapsed ? reportPanelsCollapsed : reportPanelsExpanded) }}>
            <div style={reportEmptyState}>Report data unavailable.</div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div style={reportTileWrap} ref={widgetRef}>
      <div style={reportTile}>
        <div
          style={{ ...reportTileHeader, ...reportTileHeaderClickable }}
          role="button"
          aria-expanded={!collapsed}
          onClick={toggleCollapsed}
        >
          <span>Reports</span>
          <span
            style={reportTileHeaderActions}
            onClick={(event) => event.stopPropagation()}
          >
            <input
              ref={dateInputRef}
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              style={reportTileHeaderDate}
              aria-label="Report date"
            />
          </span>
        </div>
        <div style={{ ...reportPanels, ...(collapsed ? reportPanelsCollapsed : reportPanelsExpanded) }}>
          <div style={reportPanel}>
            <div style={reportPanelTitle}>Production</div>
            <div style={reportList}>
              <ListRow label="Pending Production" value={report.PendingProductions} total={report.TotalOrders} />
              <ListRow label="Pending Pickups" value={report.PendingPickupOrder} total={report.PickupOrder} />
              <ListRow label="Pending Deliveries" value={report.PendingDeliveryOrder} total={report.DeliveryOrder} />
            </div>
          </div>
          <div style={reportPanel}>
            <div style={reportPanelTitle}>Performance</div>
            <div style={reportList}>
              <ListRow label="Daily Sales" value={report.TotalSales} total={report.SalesGoal} prefix="$" />
              <ListRow label="Weekly Sales" value={report.TotalWeekSales} total={report.TotalWeekGoal} prefix="$" />
              <ListRow label="Employees Present" value={report.PresentEmployees} total={report.TotalEmployees} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsTile;
